// src/pages/DevHub/components/DevButton.tsx
// Replaces: .dev-btn-primary, .dev-btn-secondary, .dev-copy-btn, .dev-control-btn
//
// NOTE: DevHub uses #174ea6 (darker navy blue) NOT the global --primary (#1a73e8).
// This is why we cannot use the universal shadcn Button directly — exact 1:1 match required.

import React from 'react';
import { cn } from '../../../lib/utils';

// ─── Variant types ─────────────────────────────────────────────────────────

export type DevButtonVariant = 'primary' | 'secondary' | 'copy' | 'control';
export type DevButtonState = 'default' | 'success' | 'warning' | 'danger' | 'active';

interface DevButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: DevButtonVariant;
    /** State applies semantic color overrides (used by copy/control variants) */
    state?: DevButtonState;
    className?: string;
    children: React.ReactNode;
}

// ─── Variant base classes ──────────────────────────────────────────────────

const variantClasses: Record<DevButtonVariant, string> = {
    /*
     * .dev-btn-primary:
     *   display: inline-flex; align-items: center; gap: 8px;
     *   padding: 10px 20px;
     *   background: #174ea6; color: white; border: none;
     *   border-radius: 4px;  (--dev-radius-sm)
     *   font-size: 14px; font-weight: 600; cursor: pointer;
     *   transition: background 0.2s;
     * :hover → background: #1557b0
     * :disabled → background: #dadce0; cursor: not-allowed
     */
    primary: cn(
        'inline-flex items-center gap-[8px]',
        'px-[20px] py-[10px]',
        'bg-[#174ea6] text-white border-0 rounded-[4px]',
        'text-[14px] font-semibold cursor-pointer',
        'transition-[background] duration-200',
        'hover:bg-[#1557b0]',
        'disabled:bg-[#dadce0] disabled:cursor-not-allowed disabled:pointer-events-none',
    ),

    /*
     * .dev-btn-secondary:
     *   display: inline-flex; align-items: center; gap: 8px;
     *   padding: 9px 19px;
     *   background: #ffffff; color: #54585d;
     *   border: 1px solid #dadce0; border-radius: 4px;
     *   font-size: 14px; font-weight: 600; cursor: pointer;
     *   transition: all 0.2s;
     * :hover:not(:disabled) → background: #f1f3f4; color: #202124
     * :disabled → opacity: 0.5; cursor: not-allowed
     */
    secondary: cn(
        'inline-flex items-center gap-[8px]',
        'px-[19px] py-[9px]',
        'bg-white text-[#54585d] border border-[#dadce0] rounded-[4px]',
        'text-[14px] font-semibold cursor-pointer',
        'transition-all duration-200',
        'enabled:hover:bg-[#f1f3f4] enabled:hover:text-[#202124]',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    ),

    /*
     * .dev-copy-btn:
     *   padding: 8px; background: transparent; border: none; border-radius: 8px;
     *   color: #61666b; cursor: pointer;
     *   transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
     *   display: flex; align-items: center; justify-content: center; line-height: normal;
     * :focus → outline: none
     * :hover:not(:disabled) → background: #f1f3f4; color: #174ea6
     * :disabled → opacity: 0.5; cursor: not-allowed
     */
    copy: cn(
        'p-[8px] bg-transparent border-0 rounded-[8px]',
        'text-[#61666b] cursor-pointer',
        'transition-all duration-200 [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]',
        'flex items-center justify-center leading-normal',
        'focus:outline-none',
        'enabled:hover:bg-[#f1f3f4] enabled:hover:text-[#174ea6]',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    ),

    /*
     * .dev-control-btn:
     *   Same base as .dev-copy-btn
     * .dev-control-btn.active → color: #174ea6
     * .dev-control-btn.warning → color: #f9ab00
     * .dev-control-btn.danger → color: #d93025
     */
    control: cn(
        'p-[8px] bg-transparent border-0 rounded-[8px]',
        'text-[#61666b] cursor-pointer',
        'transition-all duration-200 [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]',
        'flex items-center justify-center leading-normal',
        'focus:outline-none',
        'enabled:hover:bg-[#f1f3f4] enabled:hover:text-[#174ea6]',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    ),
};

// ─── State override classes ────────────────────────────────────────────────

const stateClasses: Record<DevButtonState, string> = {
    default: '',
    // .dev-copy-btn.success, .dev-copy-btn.success:hover → color: #0d652d !important
    success: '!text-[#0d652d] hover:!text-[#0d652d]',
    // .dev-control-btn.warning
    warning: '!text-[#f9ab00]',
    // .dev-control-btn.danger
    danger: '!text-[#d93025]',
    // .dev-control-btn.active
    active: '!text-[#174ea6]',
};

// ─── Component ─────────────────────────────────────────────────────────────

const DevButton: React.FC<DevButtonProps> = ({
    variant = 'primary',
    state = 'default',
    className,
    children,
    ...props
}) => (
    <button
        className={cn(variantClasses[variant], stateClasses[state], className)}
        {...props}
    >
        {children}
    </button>
);

export default DevButton;
